/**
 * Gate Explore map bank tap-to-filter on that casino's own S3 *-bank-ids.json.
 * Unknown casino ids must never enable tap via another casino's (e.g. LOTC) file.
 * Rendering of walls/labels/bank layers is separate — this only answers "may taps filter?".
 */

export type CasinoFloorPlanFiles = {
  casinoName: string;
  /** [walls, labels, bank-ids] */
  files: [string, string, string];
};

/**
 * Existing S3 path map (same as useMapData). No LOTC fallback here —
 * unknown ids return null so tap-to-filter cannot borrow another floor's bank file.
 */
const CASINO_FLOOR_PLAN_FILES: Record<number, CasinoFloorPlanFiles> = {
  1: { casinoName: 'lotc', files: ['lott-walls.json', 'lott-labels.json', 'lott-bank-ids.json'] },
  2: {
    casinoName: 'eureka',
    files: ['eureka-walls.json', 'eureka-labels.json', 'eureka-bank-ids.json'],
  },
  3: { casinoName: 'rwc', files: ['rwc-walls.json', 'rwc-labels.json', 'rwc-bank-ids.json'] },
  22: { casinoName: 'ssc', files: ['ssc-walls.json', 'ssc-labels.json', 'ssc-bank-ids.json'] },
  23: { casinoName: 'ach', files: ['ach-walls.json', 'ach-labels.json', 'ach-bank-ids.json'] },
  24: { casinoName: 'jjc', files: ['jjc-walls.json', 'jjc-labels.json', 'jjc-bank-ids.json'] },
};

/** This casino's own S3 floor-plan file trio, or null if unmapped (no fallback). */
export function resolveCasinoFloorPlanFiles(
  casinoId: number | null | undefined
): CasinoFloorPlanFiles | null {
  if (casinoId == null || !Number.isFinite(Number(casinoId))) return null;
  return CASINO_FLOOR_PLAN_FILES[Number(casinoId)] ?? null;
}

export function bankIdsFileName(files: CasinoFloorPlanFiles): string {
  return files.files[2];
}

/** True when payload is a usable bank-ids FeatureCollection. */
export function isValidBankIdsGeoJson(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const fc = data as { type?: unknown; features?: unknown };
  if (fc.type !== 'FeatureCollection') return false;
  if (!Array.isArray(fc.features)) return false;
  return fc.features.length > 0;
}

/**
 * Tap-to-filter is allowed only when this casino has its own mapping and that
 * casino's bank-ids GeoJSON loaded successfully. LOTC (or any other) bank data
 * present via a render fallback does not enable taps for an unmapped casino.
 */
export function isBankTapFilterEnabled(args: {
  casinoId: number | null | undefined;
  bankIdMapData: unknown;
}): boolean {
  const own = resolveCasinoFloorPlanFiles(args.casinoId);
  if (!own) return false;
  return isValidBankIdsGeoJson(args.bankIdMapData);
}

/**
 * Bank GeoJSON may enable tap only when the render fetch used this casino's own
 * S3 folder (not a LOTC fallback for an unmapped id).
 */
export function bankDataForTapFilter(
  casinoId: number | null | undefined,
  renderCasinoName: string,
  bankIdMapData: unknown
): unknown {
  const own = resolveCasinoFloorPlanFiles(casinoId);
  if (!own || own.casinoName !== renderCasinoName) return null;
  return bankIdMapData;
}

/** Session-scoped miss keys: `${casinoName}/${bankIdsFile}` — skip refetch. */
const sessionBankIdsMissCache = new Set<string>();

export function bankIdsSessionMissKey(casinoName: string, bankFile: string): string {
  return `${casinoName}/${bankFile}`;
}

export function hasSessionBankIdsMiss(casinoName: string, bankFile: string): boolean {
  return sessionBankIdsMissCache.has(bankIdsSessionMissKey(casinoName, bankFile));
}

export function markSessionBankIdsMiss(casinoName: string, bankFile: string): void {
  sessionBankIdsMissCache.add(bankIdsSessionMissKey(casinoName, bankFile));
}

/** Test helper — clears session miss cache. */
export function clearSessionBankIdsMissCacheForTests(): void {
  sessionBankIdsMissCache.clear();
}
